/**
 * Профиль: личные данные, аватар, настройки уведомлений (§5, §18.1).
 *
 * Прежде у профиля не было ни одного запроса: «Сохранить» и удаление
 * аватара показывали «подключается вместе с backend», у «Загрузить» не было
 * обработчика, а настройки уведомлений жили только в памяти страницы.
 */

import type { NotificationCategory, TaskSection } from "@taskflow/contracts";

import type { Theme } from "../themes";
import { api } from "../api";

export interface NotificationPref {
  category: NotificationCategory;
  inApp: boolean;
  email: boolean;
  /** Push на телефон (A1.3). Сайт может не присылать поле — сервер его не тронет. */
  push?: boolean;
}

export interface UiPrefs {
  /** Блоки карточки задачи, развёрнутые при её открытии. */
  expandedTaskSections: TaskSection[];
  /** Тема оформления; null — ещё не выбирали, действует тема браузера. */
  theme: Theme | null;
}

/** Сеанс на устройстве (A1.5а): по строке на вход, текущий отмечен. */
export interface MySession {
  id: string;
  client: "web" | "mobile";
  deviceName: string | null;
  appVersion: string | null;
  userAgent: string | null;
  ip: string | null;
  startedAt: string;
  startedAtLabel: string;
  lastActiveAt: string;
  lastActiveLabel: string;
  expiresAt: string;
  current: boolean;
}

export const profileApi = api.injectEndpoints({
  endpoints: (build) => ({
    updateMe: build.mutation<
      { id: string; version: number },
      {
        firstName: string;
        lastName: string;
        /** null — очистить. */
        middleName: string | null;
        position: string | null;
        version: number;
      }
    >({
      query: (params) => ({ method: "me.update", params }),
      invalidatesTags: ["Me"],
    }),

    notificationPrefs: build.query<{ data: NotificationPref[] }, void>({
      query: () => ({ method: "me.notificationPrefs.get" }),
      providesTags: ["Notification"],
    }),

    updateNotificationPrefs: build.mutation<{ ok: boolean }, { data: NotificationPref[] }>({
      query: (params) => ({ method: "me.notificationPrefs.update", params }),
      invalidatesTags: ["Notification"],
    }),

    // Настройки интерфейса хранятся на сервере, а не в браузере: человек
    // открывает задачи и с рабочей машины, и из дома, а настроил он себя,
    // а не одно из рабочих мест.
    uiPrefs: build.query<UiPrefs, void>({
      query: () => ({ method: "me.uiPrefs.get" }),
      providesTags: ["Me"],
    }),

    // Меняются только присланные настройки: тема не трогает блоки карточки.
    updateUiPrefs: build.mutation<
      UiPrefs,
      { expandedTaskSections?: TaskSection[]; theme?: Theme }
    >({
      query: (params) => ({ method: "me.uiPrefs.update", params }),
      invalidatesTags: ["Me"],
    }),

    requestAvatarUpload: build.mutation<
      { objectKey: string; uploadUrl: string; maxBytes: number },
      { filename: string; mime: string; size: number }
    >({
      query: (params) => ({ method: "me.avatar.requestUpload", params }),
    }),

    completeAvatar: build.mutation<{ avatarUrl: string }, { objectKey: string }>({
      query: (params) => ({ method: "me.avatar.complete", params }),
      invalidatesTags: ["Me"],
    }),

    deleteAvatar: build.mutation<{ ok: boolean }, void>({
      query: () => ({ method: "me.avatar.delete" }),
      invalidatesTags: ["Me"],
    }),

    mySessions: build.query<{ data: MySession[] }, void>({
      query: () => ({ method: "me.sessions.list" }),
      providesTags: ["Session"],
    }),

    revokeMySession: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "me.sessions.revoke", params }),
      invalidatesTags: ["Session"],
    }),

    // Push (A1.3): телефон сообщает токен FCM своего сеанса. Повтор с тем
    // же токеном безопасен; при выходе сервер гасит устройство сам.
    registerDevice: build.mutation<{ ok: boolean }, { token: string; appVersion?: string }>({
      query: (params) => ({ method: "me.devices.register", params }),
    }),

    unregisterDevice: build.mutation<{ ok: boolean }, { token: string }>({
      query: (params) => ({ method: "me.devices.unregister", params }),
    }),
  }),
});

export const {
  useRegisterDeviceMutation,
  useUnregisterDeviceMutation,
  useUpdateMeMutation,
  useUiPrefsQuery,
  useUpdateUiPrefsMutation,
  useNotificationPrefsQuery,
  useUpdateNotificationPrefsMutation,
  useRequestAvatarUploadMutation,
  useCompleteAvatarMutation,
  useDeleteAvatarMutation,
  useMySessionsQuery,
  useRevokeMySessionMutation,
} = profileApi;
